import React, { useEffect, useState, useCallback } from 'react';
import { Star, Loader2, Building2, TrendingUp, Clock, Briefcase, MapPin, AlertTriangle, Zap, Users, Trash2, StickyNote, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';
import { useWatchlist } from '../contexts/WatchlistContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGNAL_STYLES = {
  'Hiring Surge': 'bg-red-50 text-red-700 border-red-200',
  'Multi-Department': 'bg-purple-50 text-purple-700 border-purple-200',
  'Multi-Location': 'bg-blue-50 text-blue-700 border-blue-200',
  'Remote Friendly': 'bg-green-50 text-green-700 border-green-200',
  'Leadership Hiring': 'bg-amber-50 text-amber-700 border-amber-200',
  'High Volume': 'bg-orange-50 text-orange-700 border-orange-200',
  'Hard-to-Fill': 'bg-rose-50 text-rose-700 border-rose-200',
};

const SignalBadge = ({ signal }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${SIGNAL_STYLES[signal.type] || 'bg-gray-50 text-gray-700 border-gray-200'}`}
    data-testid={`signal-${signal.type.replace(/\s+/g, '-').toLowerCase()}`}>
    {signal.type}
  </span>
);

const CompanyCard = ({ item, onRemove, onUpdateNotes }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(item.notes || '');
  const intel = item.intelligence || {};

  const handleSaveNotes = async () => {
    await onUpdateNotes(item.company, notes);
    setEditing(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`watchlist-card-${item.company.replace(/\s+/g, '-').toLowerCase()}`}>
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="h-10 w-10 bg-[#0A1F44] rounded-lg flex items-center justify-center flex-shrink-0">
            <Building2 className="h-5 w-5 text-[#D4AF37]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-[#0A1F44] truncate">{item.company}</h3>
            <p className="text-[10px] text-gray-400">
              Added {new Date(item.added_at).toLocaleDateString()} {item.source_module && `from ${item.source_module.replace(/-/g, ' ')}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Star className="h-4 w-4 text-amber-500 fill-current" />
          <button onClick={() => onRemove(item.company)} className="text-gray-300 hover:text-red-500 transition-colors" data-testid={`remove-${item.company.replace(/\s+/g, '-').toLowerCase()}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="px-5 pb-3 grid grid-cols-4 gap-3">
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-[#0A1F44]">{intel.total_postings || 0}</div>
          <div className="text-[10px] text-gray-500">Total</div>
        </div>
        <div className="text-center p-2 bg-blue-50 rounded-lg">
          <div className="text-lg font-bold text-blue-700">{intel.recent_postings_30d || 0}</div>
          <div className="text-[10px] text-gray-500">30d</div>
        </div>
        <div className="text-center p-2 bg-green-50 rounded-lg">
          <div className="text-lg font-bold text-green-700">{intel.recent_postings_7d || 0}</div>
          <div className="text-[10px] text-gray-500">7d</div>
        </div>
        <div className="text-center p-2 bg-amber-50 rounded-lg">
          <div className="text-lg font-bold text-amber-700">{(intel.signals || []).length}</div>
          <div className="text-[10px] text-gray-500">Signals</div>
        </div>
      </div>

      {/* Signals */}
      {(intel.signals || []).length > 0 && (
        <div className="px-5 pb-3 flex flex-wrap gap-1">
          {intel.signals.map((s, i) => <SignalBadge key={i} signal={s} />)}
        </div>
      )}

      {/* Outreach Status */}
      {intel.outreach && (
        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Pipeline:</span>
            <span className="font-medium text-[#0A1F44]">{intel.outreach.pipeline_stage}</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-500">Status:</span>
            <span className="font-medium text-[#0A1F44]">{intel.outreach.status}</span>
          </div>
        </div>
      )}

      {/* Expand/Collapse */}
      <button onClick={() => setExpanded(!expanded)} className="w-full px-5 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-[#0A1F44] hover:bg-gray-100 transition-colors"
        data-testid={`expand-${item.company.replace(/\s+/g, '-').toLowerCase()}`}>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? 'Less detail' : 'Full intelligence'}
      </button>

      {expanded && (
        <div className="px-5 py-4 border-t border-gray-100 space-y-4 bg-gray-50/50">
          {/* Departments */}
          {(intel.departments || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Departments Hiring</h4>
              <div className="flex flex-wrap gap-1">
                {intel.departments.map((d, i) => (
                  <span key={i} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-700">{d}</span>
                ))}
              </div>
            </div>
          )}

          {/* Titles */}
          {(intel.titles || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Open Roles</h4>
              <div className="flex flex-wrap gap-1">
                {intel.titles.slice(0, 10).map((t, i) => (
                  <span key={i} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-700">{t}</span>
                ))}
                {intel.titles.length > 10 && <span className="px-2 py-0.5 text-[10px] text-gray-400">+{intel.titles.length - 10} more</span>}
              </div>
            </div>
          )}

          {/* Locations */}
          {intel.locations && (intel.locations.states || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Locations</h4>
              <div className="flex flex-wrap gap-1">
                {intel.locations.states.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-700">
                    <MapPin className="h-2.5 w-2.5" />{s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Industries */}
          {(intel.industries || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Industries</h4>
              <div className="flex flex-wrap gap-1">
                {intel.industries.map((ind, i) => (
                  <span key={i} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-700">{ind}</span>
                ))}
              </div>
            </div>
          )}

          {/* Salary Range */}
          {intel.salary_range && (
            <div>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Salary Range</h4>
              <p className="text-xs text-gray-700">
                {intel.salary_range.min ? `$${Math.round(intel.salary_range.min).toLocaleString()}` : 'N/A'}
                {' - '}
                {intel.salary_range.max ? `$${Math.round(intel.salary_range.max).toLocaleString()}` : 'N/A'}
              </p>
            </div>
          )}

          {/* Signal Details */}
          {(intel.signals || []).length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Signal Details</h4>
              <div className="space-y-1">
                {intel.signals.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="font-medium text-[#0A1F44]">{s.type}:</span>
                    <span className="text-gray-600">{s.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> Notes
            </h4>
            {editing ? (
              <div className="flex gap-2">
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="flex-1 text-xs border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
                  rows={2} placeholder="Add notes about this company..." data-testid="watchlist-notes-input" />
                <div className="flex flex-col gap-1">
                  <button onClick={handleSaveNotes} className="px-3 py-1 bg-[#D4AF37] text-[#0A1F44] text-xs font-medium rounded-lg hover:bg-[#c5a030]" data-testid="save-notes-btn">Save</button>
                  <button onClick={() => { setEditing(false); setNotes(item.notes || ''); }} className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <p className="text-xs text-gray-600 flex-1">{item.notes || 'No notes yet'}</p>
                <button onClick={() => setEditing(true)} className="text-xs text-[#D4AF37] hover:text-[#b89a2a] font-medium" data-testid="edit-notes-btn">Edit</button>
              </div>
            )}
          </div>

          {/* Posting Date Range */}
          {(intel.latest_posting || intel.earliest_posting) && (
            <div className="flex items-center gap-4 text-[10px] text-gray-400 pt-2 border-t border-gray-200">
              {intel.earliest_posting && <span>First seen: {intel.earliest_posting.slice(0, 10)}</span>}
              {intel.latest_posting && <span>Latest: {intel.latest_posting.slice(0, 10)}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompanyWatchlist = ({ embedded = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('recent_postings_7d');
  const [sortDir, setSortDir] = useState('desc');
  const { refresh } = useWatchlist();

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/watchlist`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRemove = async (company) => {
    const token = localStorage.getItem('backoffice_token');
    try {
      const res = await fetch(`${API_URL}/api/intelligence/watchlist/${encodeURIComponent(company)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(prev => prev.filter(d => d.company !== company));
        refresh();
        toast.success(`${company} removed from watchlist`);
      }
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleUpdateNotes = async (company, notes) => {
    const token = localStorage.getItem('backoffice_token');
    try {
      const res = await fetch(`${API_URL}/api/intelligence/watchlist/${encodeURIComponent(company)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (res.ok) {
        setData(prev => prev.map(d => d.company === company ? { ...d, notes } : d));
        toast.success('Notes saved');
      }
    } catch {
      toast.error('Failed to save notes');
    }
  };

  const sorted = [...data].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    const aVal = a.intelligence?.[sortField] || 0;
    const bVal = b.intelligence?.[sortField] || 0;
    return mul * (aVal - bVal);
  });

  const totalSignals = data.reduce((s, d) => s + (d.intelligence?.signals?.length || 0), 0);
  const surgeCount = data.filter(d => (d.intelligence?.signals || []).some(s => s.type === 'Hiring Surge')).length;
  const hardToFill = data.filter(d => (d.intelligence?.signals || []).some(s => s.type === 'Hard-to-Fill')).length;

  return (
    <IntelligenceLayout title="Watchlist" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <Star className="h-5 w-5 text-amber-500 fill-current" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="watchlist-title">Company Watchlist</h1>
            <p className="text-sm text-gray-500">Pinned companies with consolidated intelligence across all modules</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading watchlist...
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="watchlist-metrics">
              <MetricCard label="Watched Companies" value={data.length} icon={Star} />
              <MetricCard label="Total Signals" value={totalSignals} icon={AlertTriangle} />
              <MetricCard label="Hiring Surges" value={surgeCount} icon={Zap} />
              <MetricCard label="Hard-to-Fill" value={hardToFill} icon={Clock} />
            </div>

            {data.length > 0 && (
              <div className="flex items-center gap-3" data-testid="watchlist-sort">
                <span className="text-xs text-gray-500 font-medium">Sort by:</span>
                {[
                  { field: 'recent_postings_7d', label: '7-Day Activity' },
                  { field: 'recent_postings_30d', label: '30-Day Activity' },
                  { field: 'total_postings', label: 'Total Postings' },
                ].map(opt => (
                  <button key={opt.field}
                    onClick={() => { if (sortField === opt.field) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortField(opt.field); setSortDir('desc'); } }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${sortField === opt.field ? 'bg-[#D4AF37] text-[#0A1F44] shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                    data-testid={`sort-${opt.field}`}>
                    {opt.label}
                    {sortField === opt.field && (sortDir === 'desc' ? <ChevronDown className="inline h-3 w-3 ml-1" /> : <ChevronUp className="inline h-3 w-3 ml-1" />)}
                  </button>
                ))}
              </div>
            )}

            {data.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center" data-testid="watchlist-empty">
                <Star className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-[#0A1F44] mb-2">No companies pinned yet</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  Click the star icon next to any company in the intelligence modules to add them to your watchlist.
                  You'll see all their hiring signals consolidated here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="watchlist-grid">
                {sorted.map(item => (
                  <CompanyCard key={item.company} item={item} onRemove={handleRemove} onUpdateNotes={handleUpdateNotes} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default CompanyWatchlist;
